import { useCallback, useLayoutEffect, useState } from 'react';
import { isAfter, isBefore, set } from 'date-fns';
import { toDate } from 'date-fns-tz';
import { SALESFORCE_DEPLOYMENT_ID, SALESFORCE_ORG_ID, SALESFORCE_LIVE_CHAT_HOST, SALESFORCE_LIVE_CHAT_ID } from 'config';
import { loadScript } from 'lib/script';
import { captureErrorInSentry } from 'lib/errors';

const MAX_SCRIPT_LOAD_RETRIES = 10;

const useLiveAgent = ({ liveChatOnlineElement, liveChatOfflineElement }) => {
  const [hasInitialized, setInitialized] = useState(false);
  const [hasLoadingError, setLoadingError] = useState(false);

  const initializeAgent = useCallback(() => {
    if (!window.liveagent) return;

    window.liveagent.init(`${SALESFORCE_LIVE_CHAT_HOST}/chat`, SALESFORCE_DEPLOYMENT_ID, SALESFORCE_ORG_ID);

    window._laq = window._laq || [];
    window._laq.push(function () {
      window.liveagent.showWhenOnline(SALESFORCE_LIVE_CHAT_ID, document.getElementById(liveChatOnlineElement));
      window.liveagent.showWhenOffline(SALESFORCE_LIVE_CHAT_ID, document.getElementById(liveChatOfflineElement));
    });
  }, [liveChatOfflineElement, liveChatOnlineElement]);

  const loadLiveAgent = useCallback(async () => {
    try {
      await loadScript({
        url: `${SALESFORCE_LIVE_CHAT_HOST}/content/g/js/48.0/deployment.js`,
        maxRetries: MAX_SCRIPT_LOAD_RETRIES,
      });

      initializeAgent();
      setInitialized(true);
    } catch (error) {
      captureErrorInSentry(error);
      setLoadingError(true);
    }
  }, [initializeAgent]);

  const startChat = useCallback(() => {
    window.liveagent.startChat(SALESFORCE_LIVE_CHAT_ID);
  }, []);

  const isOpen = useCallback(() => {
    const timeZone = 'Australia/Melbourne'; // AEST or AEDT

    const open = toDate(set(new Date(), { hours: 8, minutes: 30, seconds: 0 }), { timeZone });
    const close = toDate(set(new Date(), { hours: 17, minutes: 30, seconds: 0 }), { timeZone });
    const now = new Date();

    return isAfter(now, open) && isBefore(now, close);
  }, []);

  useLayoutEffect(() => {
    if (!hasInitialized) loadLiveAgent();
  }, [hasInitialized, loadLiveAgent]);

  return { startChat, isOpen, hasInitialized, hasLoadingError };
};

export default useLiveAgent;
